import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { card, input, label, btnPrimary, btnGhost, h1, fmtDate } from "./ui";
import { ChevronLeft, ChevronRight, Plus, Trash2, Lock, Check, CalendarDays } from "lucide-react";

const HOURS = Array.from({ length: 13 }, (_, i) => 8 + i); // 08:00 .. 20:00
const WD = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
const pad = (n) => String(n).padStart(2, "0");
const toStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function mondayOf(base) {
  const d = new Date(base);
  const day = (d.getDay() + 6) % 7; // 0 = Monday
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}

export default function Availability() {
  const [slots, setSlots] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [weekOffset, setWeekOffset] = useState(0);
  const [busy, setBusy] = useState("");

  // quick add (custom time / recurrence)
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [repeat, setRepeat] = useState(0);

  const load = () => {
    api.get("/slots").then(({ data }) => setSlots(data)).catch(() => {});
    api.get("/sessions").then(({ data }) => setSessions(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const weekStart = useMemo(() => {
    const m = mondayOf(new Date());
    m.setDate(m.getDate() + weekOffset * 7);
    return m;
  }, [weekOffset]);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  }), [weekStart]);

  const todayStr = toStr(new Date());
  const weekLabel = `${pad(days[0].getDate())}/${pad(days[0].getMonth() + 1)} – ${pad(days[6].getDate())}/${pad(days[6].getMonth() + 1)}`;

  const cellState = (dateStr, hour) => {
    const booked = sessions.some((s) => s.date === dateStr && s.status !== "cancelada" && Number(String(s.time).split(":")[0]) === hour);
    if (booked) return { type: "booked" };
    const slot = slots.find((s) => s.date === dateStr && s.time === `${pad(hour)}:00`);
    if (slot && slot.status === "available") return { type: "available", slot };
    if (slot && slot.status === "booked") return { type: "booked" };
    return { type: "empty" };
  };

  const toggle = async (dateStr, hour, state) => {
    if (state.type === "booked") {
      toast.info("Horário ocupado por uma sessão agendada.");
      return;
    }
    const key = `${dateStr}-${hour}`;
    setBusy(key);
    try {
      if (state.type === "available") {
        await api.delete(`/slots/${state.slot.id}`);
      } else {
        await api.post("/slots", { date: dateStr, time: `${pad(hour)}:00` });
      }
      await Promise.all([
        api.get("/slots").then(({ data }) => setSlots(data)),
      ]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao atualizar horário.");
    } finally {
      setBusy("");
    }
  };

  const quickAdd = async (e) => {
    e.preventDefault();
    if (!date || !time) return toast.error("Escolha data e horário.");
    try {
      const { data } = await api.post("/slots", { date, time, repeat_weeks: Number(repeat) || 0 });
      toast.success(data.created > 1 ? `${data.created} horários disponibilizados.` : "Horário disponibilizado.");
      setTime("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao adicionar.");
    }
  };

  // custom-time slots (not on the hourly grid) so nothing stays hidden
  const customSlots = slots
    .filter((s) => !/^\d{2}:00$/.test(s.time))
    .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));

  return (
    <div data-testid="availability-page">
      <p className="font-body text-xs tracking-[0.35em] uppercase text-[#C87A5E]">Horários</p>
      <h1 className={h1}>Horários disponíveis</h1>
      <p className="font-body text-[#4A5D4E] mt-2 max-w-2xl">
        Sessões de <strong>50 minutos</strong>. Clique numa célula para <strong>liberar</strong> (fica visível no site) ou
        <strong> bloquear</strong> um horário. Horários com paciente marcado ficam como <strong>ocupado</strong> e não
        aparecem para os pacientes.
      </p>

      {/* week nav */}
      <div className="flex items-center justify-between mt-8 mb-4">
        <button onClick={() => setWeekOffset((w) => w - 1)} className={btnGhost} data-testid="week-prev"><ChevronLeft className="w-4 h-4" /> Anterior</button>
        <div className="text-center">
          <p className="font-display text-2xl text-[#2C332A]">{weekLabel}</p>
          {weekOffset !== 0 && <button onClick={() => setWeekOffset(0)} className="font-body text-xs text-[#5B6E5B] hover:underline" data-testid="week-today">voltar para a semana atual</button>}
        </div>
        <button onClick={() => setWeekOffset((w) => w + 1)} className={btnGhost} data-testid="week-next">Próxima <ChevronRight className="w-4 h-4" /></button>
      </div>

      {/* grid */}
      <div className={`${card} p-4 overflow-x-auto`}>
        <div className="min-w-[720px]">
          <div className="grid grid-cols-[64px_repeat(7,1fr)] gap-1.5">
            <div />
            {days.map((d, i) => {
              const ds = toStr(d);
              const isToday = ds === todayStr;
              return (
                <div key={ds} className={`text-center py-2 rounded-lg ${isToday ? "bg-[#5B6E5B] text-[#FDFBF7]" : "text-[#2C332A]"}`}>
                  <p className="font-body text-[11px] uppercase tracking-wide opacity-80">{WD[i]}</p>
                  <p className="font-display text-lg leading-none mt-0.5">{pad(d.getDate())}</p>
                </div>
              );
            })}

            {HOURS.map((h) => (
              <RowFragment key={h} hour={h} days={days} cellState={cellState} toggle={toggle} busy={busy} />
            ))}
          </div>

          {/* legend */}
          <div className="flex flex-wrap gap-4 mt-4 font-body text-xs text-[#7A8A7A]">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#5B6E5B]" /> livre (visível no site)</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#F5F0E6] border border-[#5B6E5B]/20" /> bloqueado (clique p/ liberar)</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#C87A5E]" /> ocupado (paciente marcado)</span>
          </div>
        </div>
      </div>

      {/* quick add custom time / recurrence */}
      <form onSubmit={quickAdd} className={`${card} p-6 mt-6 flex flex-wrap items-end gap-4`} data-testid="slot-form">
        <div><p className="font-display text-lg text-[#2C332A] w-full">Adicionar horário específico</p></div>
        <div className="w-full flex flex-wrap items-end gap-4">
          <div><label className={label}>Data</label><input type="date" className={input} value={date} onChange={(e) => setDate(e.target.value)} data-testid="slot-date" /></div>
          <div><label className={label}>Horário</label><input type="time" className={input} value={time} onChange={(e) => setTime(e.target.value)} data-testid="slot-time" /></div>
          <div><label className={label}>Repetir (semanas)</label><input type="number" min="0" max="52" className={`${input} w-32`} value={repeat} onChange={(e) => setRepeat(e.target.value)} placeholder="0" data-testid="slot-repeat" /></div>
          <button className={btnPrimary} type="submit" data-testid="add-slot"><Plus className="w-4 h-4" /> Disponibilizar</button>
        </div>
      </form>

      {customSlots.length > 0 && (
        <div className={`${card} p-6 mt-6`}>
          <p className="font-display text-lg text-[#2C332A] mb-4 flex items-center gap-2"><CalendarDays className="w-5 h-5 text-[#5B6E5B]" /> Outros horários (fora da grade)</p>
          <div className="flex flex-wrap gap-3">
            {customSlots.map((s) => (
              <div key={s.id} className="inline-flex items-center gap-3 rounded-full bg-[#F5F0E6] pl-4 pr-2 py-1.5" data-testid="slot-item">
                <span className="font-body text-sm text-[#2C332A]">{fmtDate(s.date)} · {s.time}</span>
                <span className={`text-xs ${s.status === "available" ? "text-[#4A5D4E]" : "text-[#B2644D]"}`}>{s.status === "available" ? "livre" : "ocupado"}</span>
                <button onClick={async () => { await api.delete(`/slots/${s.id}`); load(); }} className="text-[#C87A5E] hover:text-[#B2644D] ml-1" data-testid="delete-slot"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RowFragment({ hour, days, cellState, toggle, busy }) {
  return (
    <>
      <div className="flex items-center justify-end pr-2 font-body text-xs text-[#7A8A7A]">{pad(hour)}:00</div>
      {days.map((d) => {
        const ds = toStr(d);
        const st = cellState(ds, hour);
        const key = `${ds}-${hour}`;
        const loading = busy === key;
        const base = "h-11 rounded-lg flex items-center justify-center transition-colors duration-200 text-xs font-body";
        if (st.type === "booked") {
          return (
            <div key={key} className={`${base} bg-[#C87A5E]/90 text-[#FDFBF7] cursor-not-allowed`} data-testid={`cell-booked-${ds}-${hour}`} title="Ocupado por uma sessão">
              <Lock className="w-3.5 h-3.5" />
            </div>
          );
        }
        if (st.type === "available") {
          return (
            <button key={key} onClick={() => toggle(ds, hour, st)} disabled={loading} data-testid={`cell-available-${ds}-${hour}`}
              className={`${base} bg-[#5B6E5B] text-[#FDFBF7] hover:bg-[#B2644D] group`} title="Clique para bloquear">
              <Check className="w-4 h-4 group-hover:hidden" />
              <span className="hidden group-hover:inline">bloquear</span>
            </button>
          );
        }
        return (
          <button key={key} onClick={() => toggle(ds, hour, st)} disabled={loading} data-testid={`cell-empty-${ds}-${hour}`}
            className={`${base} bg-[#F5F0E6] text-[#A8B0A2] hover:bg-[#5B6E5B]/15 hover:text-[#5B6E5B]`} title="Clique para liberar">
            <Plus className="w-4 h-4" />
          </button>
        );
      })}
    </>
  );
}
